"use client";

import { Account, IxSigner, useAuthStore } from "@shared/src/stores/useAuthStore";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ReactNode, createContext, useCallback, useContext, useEffect } from "react";
import { useAccount, useDisconnect } from "wagmi";
import { useChain, useSigner } from "@account-kit/react";
import { ixToast } from "@shared/src/utils/ixToast";
import { createWalletClient, custom, EIP1193Provider, Hex } from "viem";
import { WalletClientSigner } from "@aa-sdk/core";
import { useP2PStore } from "@shared/src/stores/useP2PStore";
import useAuth from "@shared/src/hooks/auth/useAuth";
import chains, { IxChain } from "@shared/src/config/chains";
import { SiwewAuthParams } from "@shared/src/hooks/auth/types";
import { analytics } from "@shared/src/config/firebase";
import { logEvent } from "firebase/analytics";
import { CHAIN_ID_KEY } from "@shared/src/lib/queryKeys";
import usePageFocus from "@shared/src/hooks/usePageFocus";
import { pollForAuthDetails } from "@shared/src/lib/auth";
import { wagmiConfig } from "@shared/src/config/wagmiConfig";
import { getAccount, watchAccount } from "@wagmi/core";
import { getSdk, QuickTradeNotificationData } from "@web/app/lib/sdkClient";
import { useModalStore } from "@shared/src/stores/useModalStore";
import { useNotificationStore } from "@shared/src/stores/useNotificationStore";
import { initHost, useHostStore } from "@shared/src/stores/useHostStore";
import { IxAnalytics } from "@shared/src/utils/analytics/IxAnalytics";
import { HOME_PAGE, QUICK_TRADE_PAGE } from "@shared/src/config/page-links";
import { APP_VERSION } from "@shared/src/utils/constants";
import { useMiniKit } from "@coinbase/onchainkit/minikit";
import { compareSemver } from "@sdk/utils";
import { base } from "viem/chains";
import { AlchemySigner } from "@account-kit/core";

interface InitializerContextType {
  isInitialised: boolean;
}

const InitializerContext = createContext<InitializerContextType>({
  isInitialised: false,
});

export const useInitializer = () => useContext(InitializerContext);

interface InitializerProviderProps {
  children: ReactNode;
}

const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

// Initialize analytics singleton once (browser only)
const ixAnalyticsInstance = IxAnalytics.init({ firebase: analytics as any, clarityProjectId: projectId });
const checkIfScaSignedOut = async (activeAccount_: Account) => {
  console.log("checking if alchemy signed out");
  try {
    const det = await pollForAuthDetails();
    return false;
  } catch {
    ixAnalyticsInstance.trackAutoAlchemySignout(activeAccount_.address, activeAccount_.username);
    return true;
  }
};

const isChainSupported = () => {
  const activeChain = useHostStore.getState().activeChain;
  const walletChainId = getAccount(wagmiConfig).chainId;
  return activeChain?.id === walletChainId;
};
const sdk = getSdk();

export const InitializerProvider = ({ children }: InitializerProviderProps) => {
  const { chainId: wagmiChainId, chain: wagmiChain, address, connector, status } = useAccount();
  const router = useRouter();
  const setAndSwitchChain = useHostStore(state => state.setAndSwitchChain);
  const searchParams = useSearchParams();
  const { disconnect } = useDisconnect();
  const {
    activeAccount,
    signOut,
    setActiveAccount,
    isInitialised,
    setIsInitialised,
    alchemyOauthPrepared,
    setAlchemyOauthPrepared,
  } = useAuthStore();
  const { chain: alchemyChain } = useChain();
  const alchemySigner = useSigner() as IxSigner;
  const { handleIxAuth } = useAuth();
  const modal = useModalStore();
  const { setFrameReady, isFrameReady } = useMiniKit();

  useEffect(() => {
    if (!isFrameReady) setFrameReady();
  }, [isFrameReady, setFrameReady]);

  useEffect(() => {
    // Only load Eruda in development and not on localhost
    if (
      typeof window !== "undefined" &&
      (searchParams?.get("debug") === "true" || process.env.NODE_ENV === "development")
    ) {
      console.log("[Initializer] Loading Eruda debugging tools for development environment");
      import("eruda").then(eruda => eruda.default.init());
    }
  }, []);

  // Update HostStore with actual Next.js router instance once it's available
  useEffect(() => {
    initHost({
      context: "webApp",
      router: {
        push: router.push,
        replace: router.replace,
        back: router.back,
        goToHome: () => {
          router.push(HOME_PAGE);
        },
        refresh: () => {
          router.refresh();
        },
        prefetch: router.prefetch,
      },
      sdk: getSdk(),
      analytics: ixAnalyticsInstance,
    });
  }, [router]);

  useEffect(() => {
    function isSafari() {
      const ua = navigator.userAgent.toLowerCase();
      return ua.includes("safari") && !ua.includes("chrome") && !ua.includes("android");
    }

    const oauth = searchParams?.get("oauth");
    if (!searchParams) return;

    if (oauth === "true" && alchemySigner) {
      ixToast.dismiss();
      const authProviderId = searchParams.get("authProviderId") as SiwewAuthParams["authProviderId"];
      const connectorIcon = searchParams.get("connectorIcon") as SiwewAuthParams["connectorIcon"];
      router.push("/");
      handleIxAuth(
        alchemySigner,
        { type: "oauth", authProviderId, connectorIcon, connectorId: "alchemy" },
        connectorIcon,
      );
    }
  }, [handleIxAuth, alchemySigner, router, searchParams]);

  // monitor network changes
  useEffect(() => {
    let unsubscribe: () => void;
    if (activeAccount?.type !== "eoa") {
      return;
    } else {
      //set Listener for chain change
      const handleChainChange = (newChainId: number) => {
        const newChain = chains.find(c => c.id === Number(newChainId));
        if (!newChain) {
          setAndSwitchChain(null);
          useModalStore.getState().setNetworkModalOpen(true);
          return;
        }
        useHostStore.getState().setAndSwitchChain(newChain.id);
      };
      const handleDisconnect = () => {
        signOut(disconnect);
      };

      unsubscribe = watchAccount(wagmiConfig, {
        onChange: data => {
          if (data.chainId) handleChainChange(data.chainId);
          if (data.isDisconnected) handleDisconnect();
        },
      });
    }

    return () => {
      unsubscribe();
    };
  }, [activeAccount, disconnect, setAndSwitchChain, signOut]);

  //set currency and check chain
  useEffect(() => {
    const unsubscribe = sdk.subscribe("AUTHENTICATED", event => {
      const setCurrency = () => {
        const currency = sdk.auth.kyc?.currency;
        const currencies = sdk.config.currencies;

        if (!currencies) {
          throw new Error("no Currencies");
        }
        const startCurrency = currencies.find(c => c.symbol === currency)?.symbol || null;
        // Then set currency using the proper method to trigger sorting logic
        if (startCurrency) {
          useP2PStore.getState().setCurrency(startCurrency);
        }
      };
      const checkChain = () => {
        if (event.data.walletClient.account?.type === "json-rpc" && event.data.account) {
          if (!wagmiChain) {
            useModalStore.getState().setNetworkModalOpen(true);
            setAndSwitchChain(null);
          } else {
            setAndSwitchChain(wagmiChain.id);
          }
        } else {
          const oldChainId = localStorage.getItem(CHAIN_ID_KEY);
          const chainToset = Number(oldChainId) || alchemyChain?.id;
          setAndSwitchChain(chainToset);
        }
      };
      setCurrency();
      checkChain();
    });
    return unsubscribe;
  }, [alchemyChain?.id, setAndSwitchChain, wagmiChain, wagmiChainId]);

  // initialize p2p store
  useEffect(() => {
    const unsubscribe = sdk.subscribe("INITIALIZED", event => {
      const initialize = async () => {
        try {
          // First initialize the basic data without currency
          useP2PStore.setState({
            networks: event.data.networks,
            currencies: event.data.currencies,
            paymentMethods: event.data.paymentMethods,
            tokens: event.data.tokens,
            isInitialized: true,
          });
        } catch (error: any) {
          console.error("Initialization failed:", error);
          ixToast.error(error);
        }
      };
      initialize();
    });
    return unsubscribe;
  }, []);

  // authenticate
  useEffect(() => {
    if (isInitialised) return;
    const retrieveSigner = async () => {
      const storedAccount = localStorage.getItem("activeAccount");
      if (!storedAccount) {
        setIsInitialised(true);
        return;
      }
      let _signer: IxSigner | null = alchemySigner;
      try {
        const parsedAccount: Account = JSON.parse(storedAccount);
        // Get the appropriate signer based on the provider
        if (parsedAccount.type === "eoa") {
          const provider = await connector?.getProvider?.();
          if (!provider) return;
          parsedAccount.authParams.provider = provider as EIP1193Provider;
          const client = createWalletClient({
            transport: custom(provider as any),
            chain: base,
          });
          _signer = new WalletClientSigner(client, "wallet");
        } else {
          if (!_signer) return;
        }

        // Reconstruct the account with the new signer
        const account = {
          ...parsedAccount,
          signer: _signer,
        };

        await setActiveAccount(account);
        setIsInitialised(true);
      } catch (error) {
        setIsInitialised(false);
        return null;
      }
    };

    retrieveSigner();
  }, [alchemySigner, connector, isInitialised, setActiveAccount, signOut, setIsInitialised]);

  useEffect(() => {
    // Handle application versioning for client-side storage
    const VERSION_KEY = "appVersion";

    // Helper to read version from cookies (string, fallback "0.0.0")
    const getCookieVersion = (): string => {
      if (typeof document === "undefined") return "0.0.0";
      const match = document.cookie.match(new RegExp("(?:^|; )" + VERSION_KEY + "=([^;]*)"));
      return match ? decodeURIComponent(match[1]) : "0.0.0";
    };

    // Helper to set version cookie
    const setCookieVersion = (version: string) => {
      if (typeof document === "undefined") return;
      // Set cookie for 1 year
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = `${VERSION_KEY}=${encodeURIComponent(
        version,
      )}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
    };

    if (typeof window === "undefined") return;

    const appVersion = APP_VERSION || "0.0.0";
    const storedLocalVersion = (localStorage.getItem(VERSION_KEY) || "0.0.0").trim();
    const storedCookieVersion = getCookieVersion().trim();

    // Decide the authoritative stored version (the higher one if they diverge)
    const storedVersion =
      compareSemver(storedLocalVersion, storedCookieVersion) >= 0 ? storedLocalVersion : storedCookieVersion;

    console.log("[Initializer] Version compatibility check:", { current: appVersion, stored: storedVersion });

    const cmp = compareSemver(appVersion, storedVersion);
    if (cmp !== 0) {
      const direction = cmp > 0 ? "upgrade" : "downgrade";
      console.log(
        `[Initializer] Application ${direction} detected (${storedVersion} → ${appVersion}). Clearing browser storage to prevent data corruption.`,
      );
      // Clear volatile storage to avoid stale cached data
      try {
        localStorage?.clear();
        sessionStorage?.clear();
      } catch (e) {
        console.warn("[Initializer] Storage clear operation failed:", e);
      }
      // Persist new version after clearing
      localStorage?.setItem(VERSION_KEY, appVersion);
      setCookieVersion(appVersion);
      // Force a reload only on upgrade (avoid reload loops if downgrade artifacts)
      if (cmp > 0) {
        window.location.reload();
      }
      return;
    }

    // If versions match but not persisted uniformly, sync them
    if (storedLocalVersion !== appVersion) localStorage?.setItem(VERSION_KEY, appVersion);
    if (storedCookieVersion !== appVersion) setCookieVersion(appVersion);
  }, []);

  const pathname = usePathname(); // Current route

  // analytics
  useEffect(() => {
    if (!analytics) return; // Skip if analytics isn't initialized

    // Combine pathname and search params for full URL
    const url = `${pathname}${searchParams?.toString() ? `?${searchParams}` : ""}`;

    // Log page view event
    logEvent(analytics, "page_view", {
      page_path: url,
      page_title: document.title, // Optional: grabs the page title
    });
  }, [pathname, searchParams]); // Re-run when route or query params change

  // Check if the user is alchemy signed out
  useEffect(() => {
    // Check if the user is signed out
    const checkIfSignedOut = async () => {
      if (activeAccount?.type === "sca") {
        const isSignedOut = await checkIfScaSignedOut(activeAccount);
        if (isSignedOut) {
          signOut();
        }
      }
    };
    checkIfSignedOut();
  }, [activeAccount, signOut]);

  usePageFocus(() => {
    console.log("Page has regained focus");
    if (activeAccount?.backendAccount.isQuickTradeMerchant) {
      fetchQuickTradeNotification();
    }
    if (activeAccount?.type === "sca") {
      checkIfScaSignedOut(activeAccount).then(isSignedOut => {
        if (isSignedOut) {
          signOut();
        }
      });
    }
  });

  const fetchQuickTradeNotification = useCallback(async () => {
    if (!activeAccount) return;
    const { data } = (await sdk?.orders.getUserQuickTrade()) || {};
    console.log({ quicktradeData: data, activeAccount });
    if (data?.quickTrade?.status === "pending") {
      useNotificationStore.getState().setQuickTradeNotification(data);
      if (data.quickTrade?.trader.toLowerCase() === activeAccount?.address.toLowerCase()) {
        ixToast.default("Quick trade in progress", {
          title: "Quick trade",
        });
        router.push(QUICK_TRADE_PAGE);
      } else {
        ixToast.success("Quick trade Order received", {
          title: "Quick trade",
        });
      }
    } else {
      useNotificationStore.getState().setQuickTradeNotification(null);
    }
  }, [router, activeAccount]);

  useEffect(() => {
    // Listen for QuickTrade events via SDK notification system
    if (!activeAccount) return;
    fetchQuickTradeNotification();
    const handleQuickTradeEvent = (data: QuickTradeNotificationData) => {
      switch (data.quickTrade?.status) {
        case "pending":
          if (data.quickTrade.trader.toLowerCase() !== activeAccount?.address.toLowerCase()) {
            ixToast.default("New quick trade Order received", {
              title: "New quick trade",
            });
            useNotificationStore.getState().setQuickTradeNotification(data);
          }
      }
    };

    const unsubscribe = sdk.subscribe("QUICK_TRADE", event => {
      handleQuickTradeEvent(event.data as QuickTradeNotificationData);
    });

    return unsubscribe;
  }, [activeAccount]);

  useEffect(() => {
    const unsubscribe = sdk?.subscribe("SIGN_JSON_RPC_REQUEST", event => {
      useModalStore.getState().showModal({ type: "loading", title: "Action Required", message: event.data.message });
    });
    return unsubscribe;
  }, [sdk]);

  // Prepare Alchemy OAuth popup in advance (if applicable)
  useEffect(() => {
    const checkAndPrepareOauth = async () => {
      if (!alchemyOauthPrepared) {
        try {
          if (alchemySigner) {
            await (alchemySigner as AlchemySigner).preparePopupOauth();
            setAlchemyOauthPrepared(true);
          }
        } catch (error) {
          console.error("[Initializer] OAuth preparation failed:", error);
        }
      }
    };

    checkAndPrepareOauth();
  }, [alchemyOauthPrepared, alchemySigner, setAlchemyOauthPrepared]);

  return <InitializerContext.Provider value={{ isInitialised }}>{children}</InitializerContext.Provider>;
};
